/* eslint-disable */
/**
 * GrafLinje — Sparkline/linjegraf. Bruger Recharts (allerede i package.json).
 *
 * Props:
 *   data          Array<{ label, værdi }> | Array<number>
 *   farve         string  — linjens farve (default: STATUS.konfigureret)
 *   højde         number  — grafens højde i px (default: 80)
 *   fyldt         bool    — fyld under linjen (default: true)
 *   viserTooltip  bool    — vis tooltip på hover (default: true)
 *   viserYAkse    bool    — vis Y-akse labels (default: false for sparklines, true for større grafer)
 *   viserXAkse    bool    — vis X-akse labels (default: false for sparklines)
 *   tomTekst      string  — vises når data er tomt
 *   style         obj
 *
 * Normaliserer input: accepterer både { label, værdi } og { dato, antal/haendelser/... }
 *
 * OS-15: YAxis + Tooltip tilgængeligt via props — defaulthøjde øget til 120px
 * når akser vises for at give plads til labels.
 */

import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { STATUS, TEKST, BG } from './tokens';
import TomTilstand from './TomTilstand';

// Normalisér til { label, værdi }
function normaliser(data) {
  if (!Array.isArray(data) || !data.length) return [];
  if (typeof data[0] === 'number') {
    return data.map((v, i) => ({ label: String(i + 1), værdi: v }));
  }
  return data.map(d => ({
    label: d.label || d.dato || '',
    værdi: d.værdi ?? d.antal ?? d.haendelser ?? d.aktive_sager ?? 0,
  }));
}

const TilpassetTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(8,14,34,0.95)',
      border:     '1px solid rgba(255,255,255,0.12)',
      borderRadius: 6,
      padding:    '6px 10px',
      fontSize:   12,
      color:      TEKST.primær,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{label}</div>
      <div>{payload[0]?.value}</div>
    </div>
  );
};

export default function GrafLinje({
  data = [],
  farve,
  højde = 80,
  fyldt = true,
  viserTooltip = true,
  viserYAkse = false,
  viserXAkse = false,
  tomTekst = 'Ikke nok data til graf',
  style = {},
}) {
  const fv = farve || STATUS.konfigureret;
  const normaliseret = normaliser(data);

  // OS-15: Øg standardhøjde når akser vises for at give labels plads
  const effektivHøjde = (viserYAkse || viserXAkse) ? Math.max(højde, 120) : højde;

  // Margin: venstre side giver plads til Y-akse labels når de er aktive
  const margin = {
    top:    4,
    right:  8,
    left:   viserYAkse ? 0 : 4,
    bottom: viserXAkse ? 16 : 0,
  };

  const yAkseStyle = {
    tick:     { fill: TEKST.sekundær, fontSize: 10 },
    tickLine: false,
    axisLine: false,
  };

  const xAkseStyle = {
    tick:      { fill: TEKST.sekundær, fontSize: 10 },
    tickLine:  false,
    axisLine:  { stroke: 'rgba(255,255,255,0.08)' },
    interval:  'preserveStartEnd',
  };

  if (normaliseret.length < 2) {
    return (
      <TomTilstand
        titel={tomTekst}
        besked="Kræver mindst 2 datapunkter."
        ikon="↗"
        badge="KONFIGURERET"
        style={{ padding: '16px', minHeight: effektivHøjde, ...style }}
      />
    );
  }

  if (fyldt) {
    return (
      // minHeight sikrer at flex/grid-parent aldrig kollapser containeren til 0px,
      // hvilket ville få ResponsiveContainer til at rapportere width/height=-1.
      // height={effektivHøjde} (tal) på ResponsiveContainer undgår "100%" som fejler
      // i overflow:auto-containere (fx OS-dashboardets fixed overlay).
      <div style={{ width: '100%', height: effektivHøjde, minHeight: effektivHøjde, display: 'block', ...style }}>
        <ResponsiveContainer width="100%" height={effektivHøjde}>
          <AreaChart data={normaliseret} margin={margin}>
            <defs>
              <linearGradient id={`grad-${fv.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={fv} stopOpacity={0.22} />
                <stop offset="95%" stopColor={fv} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            {viserTooltip && <Tooltip content={<TilpassetTooltip />} />}
            {/* OS-15: XAxis og YAxis aktiveres via props */}
            <XAxis dataKey="label" hide={!viserXAkse} {...xAkseStyle} />
            {viserYAkse && (
              <YAxis
                {...yAkseStyle}
                width={36}
                tickFormatter={v => v >= 1000 ? `${Math.round(v/1000)}k` : String(v)}
              />
            )}
            <Area
              type="monotone"
              dataKey="værdi"
              stroke={fv}
              strokeWidth={2}
              fill={`url(#grad-${fv.replace('#','')})`}
              dot={false}
              activeDot={{ r: 4, fill: fv, stroke: BG.app, strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: effektivHøjde, minHeight: effektivHøjde, display: 'block', ...style }}>
      <ResponsiveContainer width="100%" height={effektivHøjde}>
        <LineChart data={normaliseret} margin={margin}>
          {viserTooltip && <Tooltip content={<TilpassetTooltip />} />}
          <XAxis dataKey="label" hide={!viserXAkse} {...xAkseStyle} />
          {viserYAkse && (
            <YAxis
              {...yAkseStyle}
              width={36}
              tickFormatter={v => v >= 1000 ? `${Math.round(v/1000)}k` : String(v)}
            />
          )}
          <Line
            type="monotone"
            dataKey="værdi"
            stroke={fv}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: fv, stroke: BG.app, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
